
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User, Settings, Bell, Gift, Calendar, Trophy, Users, Star } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import GameHistory from '@/components/GameHistory';
import AchievementSystem from '@/components/AchievementSystem';
import PushNotifications from '@/components/PushNotifications';
import DrawCalendar from '@/components/DrawCalendar';
import ReferralSystem from '@/components/ReferralSystem';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const userProfile = {
    name: 'John Doe',
    email: 'john.doe@email.com',
    phone: '+61 4XX XXX XXX',
    memberSince: 'January 2024',
    level: 7,
    totalWinnings: 2675,
    gamesPlayed: 45,
    favoriteGame: 'PowerBall'
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Profile Header */}
          <div className="mb-8">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center">
                <User className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{userProfile.name}</h1>
                <p className="text-gray-600">Level {userProfile.level} Player</p>
                <Badge className="bg-yellow-100 text-yellow-800 mt-1">VIP Member</Badge>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <Trophy className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Total Winnings</p>
                  <p className="text-xl font-bold text-yellow-600">${userProfile.totalWinnings}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4 text-center">
                  <Star className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Games Played</p>
                  <p className="text-xl font-bold text-blue-600">{userProfile.gamesPlayed}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4 text-center">
                  <Gift className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Current Level</p>
                  <p className="text-xl font-bold text-green-600">{userProfile.level}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Favorite Game</p>
                  <p className="text-xl font-bold text-purple-600">{userProfile.favoriteGame}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 md:grid-cols-6">
              <TabsTrigger value="overview" className="flex items-center gap-1">
                <User className="w-4 h-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                History
              </TabsTrigger>
              <TabsTrigger value="achievements" className="flex items-center gap-1">
                <Trophy className="w-4 h-4" />
                Achievements
              </TabsTrigger>
              <TabsTrigger value="calendar" className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="referrals" className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Referrals
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-1">
                <Settings className="w-4 h-4" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Account Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-gray-700">Full Name</Label>
                      <p className="font-medium text-gray-900">{userProfile.name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-700">Email Address</Label>
                      <p className="font-medium text-gray-900">{userProfile.email}</p>
                    </div>
                    <div>
                      <Label className="text-gray-700">Phone Number</Label>
                      <p className="font-medium text-gray-900">{userProfile.phone}</p>
                    </div>
                    <div>
                      <Label className="text-gray-700">Member Since</Label>
                      <p className="font-medium text-gray-900">{userProfile.memberSince}</p>
                    </div>
                  </CardContent>
                </Card>

                <PushNotifications />
              </div>
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <GameHistory />
            </TabsContent>

            <TabsContent value="achievements" className="mt-6">
              <AchievementSystem />
            </TabsContent>

            <TabsContent value="calendar" className="mt-6">
              <DrawCalendar />
            </TabsContent>

            <TabsContent value="referrals" className="mt-6">
              <ReferralSystem />
            </TabsContent>

            <TabsContent value="settings" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input id="name" defaultValue={userProfile.name} />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input id="email" type="email" defaultValue={userProfile.email} />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" defaultValue={userProfile.phone} />
                    </div>
                    <Button className="bg-yellow-600 hover:bg-yellow-700">
                      Update Profile
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Security Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input id="current-password" type="password" />
                    </div>
                    <div>
                      <Label htmlFor="new-password">New Password</Label>
                      <Input id="new-password" type="password" />
                    </div>
                    <div>
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input id="confirm-password" type="password" />
                    </div>
                    <Button className="bg-yellow-600 hover:bg-yellow-700">
                      Change Password
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Profile;
